import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationBuilderAPI } from '../../api/applicationBuilderAPI';
import { loadSavedForm, saveForm, clearSavedForm, mergeSavedForm } from '../../utils/savedForms';
import './FormStyles.css';
import './CoverLetterFormStyles.css';

const makeDefaultFormData = () => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  physical_address: '',
  company_name: '',
  position_name: '',
  recruiter_name: '',
  recruiter_title: '',
  company_address: '',
  current_status: '',
  referral_source: '',
  past_experience: '',
  gained_skills: '',
  project_description: '',
  project_result: '',
  why_company: '',
  background_summary: '',
  skill_summary: ''
});

const CoverLetterForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [restoredFromPrevious] = useState(() => Boolean(loadSavedForm('cover_letter')));
  const [formData, setFormData] = useState(() =>
    mergeSavedForm(makeDefaultFormData(), loadSavedForm('cover_letter'))
  );

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await applicationBuilderAPI.getProfile();
        if (response.profile_data) {
          setProfileData(response.profile_data);
          // The last cover letter they built wins over profile auto-fill.
          if (restoredFromPrevious) return;
          setFormData(prev => ({
            ...prev,
            first_name: response.profile_data.first_name || '',
            last_name: response.profile_data.last_name || '',
            email: response.profile_data.email || '',
            phone: response.profile_data.phone || '',
            physical_address: response.profile_data.physical_address || ''
          }));
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadProfile();
  }, [restoredFromPrevious]);

  const startFresh = () => {
    clearSavedForm('cover_letter');
    const blank = makeDefaultFormData();
    if (!profileData) {
      setFormData(blank);
      return;
    }
    // Match a first-time visit, which auto-fills from the profile.
    setFormData({
      ...blank,
      first_name: profileData.first_name || '',
      last_name: profileData.last_name || '',
      email: profileData.email || '',
      phone: profileData.phone || '',
      physical_address: profileData.physical_address || ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await applicationBuilderAPI.generateDocument({
        document_type: 'cover_letter',
        ...formData,
        use_ai: true
      });

      saveForm('cover_letter', formData);
      navigate('/application-builder/editor/cover_letter', { 
        state: { documentData: response } 
      });
    } catch (error) {
      console.error('Error generating cover letter:', error);
      alert(error?.data?.error === 'save_failed'
        ? error.data.message
        : 'Failed to generate cover letter. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container cl-form-container">
      <div className="form-wrapper cl-form-wrapper">
        <div className="form-header cl-form-header">
          <h1>Cover Letter Builder</h1>
          <p>Create a personalized cover letter for your application</p>
          {restoredFromPrevious && (
            <p className="builder-restored-note">
              Filled in from the last cover letter you built.
              <button type="button" className="builder-restored-reset" onClick={startFresh}>
                Start fresh
              </button>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="application-form cl-form">
          {/* Personal Information */}
          <section className="form-section cl-section">
            <h2 className="section-title cl-section-title">Your Information</h2>
            <div className="input-group cl-field-row">
              <div className="input-field cl-field">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  required
                />
              </div>
              <div className="input-field cl-field">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="input-group cl-field-row">
              <div className="input-field cl-field">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              <div className="input-field cl-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
            </div>
            <div className="input-field cl-field full-width">
              <label>Address</label>
              <input
                type="text"
                value={formData.physical_address}
                onChange={(e) => handleInputChange('physical_address', e.target.value)}
              />
            </div>
          </section>

          {/* Company Information */}
          <section className="form-section cl-section">
            <h2 className="section-title cl-section-title">Company & Position</h2>
            <div className="input-group cl-field-row">
              <div className="input-field cl-field">
                <label>Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  required
                />
              </div>
              <div className="input-field cl-field">
                <label>Position Name</label>
                <input
                  type="text"
                  value={formData.position_name}
                  onChange={(e) => handleInputChange('position_name', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="input-group cl-field-row">
              <div className="input-field cl-field">
                <label>Recruiter Name (optional)</label>
                <input
                  type="text"
                  value={formData.recruiter_name}
                  onChange={(e) => handleInputChange('recruiter_name', e.target.value)}
                  placeholder="Leave blank for 'Hiring Manager'"
                />
              </div>
              <div className="input-field cl-field">
                <label>Recruiter Title (optional)</label>
                <input
                  type="text"
                  value={formData.recruiter_title}
                  onChange={(e) => handleInputChange('recruiter_title', e.target.value)}
                />
              </div>
            </div>
            <div className="input-field cl-field full-width">
              <label>Company Address (optional)</label>
              <input
                type="text"
                value={formData.company_address}
                onChange={(e) => handleInputChange('company_address', e.target.value)}
              />
            </div>
          </section>

          {/* Content */}
          <section className="form-section cl-section">
            <h2 className="section-title cl-section-title">Cover Letter Content</h2>
            <div className="input-field cl-field full-width">
              <label>Current Status</label>
              <input
                type="text"
                value={formData.current_status}
                onChange={(e) => handleInputChange('current_status', e.target.value)}
                placeholder="e.g., Final year student at University of Oxford"
                required
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>How did you hear about this role?</label>
              <input
                type="text"
                value={formData.referral_source}
                onChange={(e) => handleInputChange('referral_source', e.target.value)}
                placeholder="Career fair, LinkedIn, employee referral..."
                required
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>Past Experience</label>
              <textarea
                rows="3"
                value={formData.past_experience}
                onChange={(e) => handleInputChange('past_experience', e.target.value)}
                placeholder="Describe your relevant experience"
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>Skills Gained</label>
              <textarea
                rows="2"
                value={formData.gained_skills}
                onChange={(e) => handleInputChange('gained_skills', e.target.value)}
                placeholder="What skills have you developed?"
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>Why This Company?</label>
              <textarea
                rows="3"
                value={formData.why_company}
                onChange={(e) => handleInputChange('why_company', e.target.value)}
                placeholder="What attracts you to this company?"
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>Project Description</label>
              <textarea
                rows="2"
                value={formData.project_description}
                onChange={(e) => handleInputChange('project_description', e.target.value)}
                placeholder="Describe a relevant project or achievement"
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>Project Result</label>
              <textarea
                rows="2"
                value={formData.project_result}
                onChange={(e) => handleInputChange('project_result', e.target.value)}
                placeholder="What was the outcome or impact?"
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>Background Summary</label>
              <textarea
                rows="3"
                value={formData.background_summary}
                onChange={(e) => handleInputChange('background_summary', e.target.value)}
                placeholder="• 2+ years in investment banking&#10;• Experience across M&A and capital markets&#10;• Strong track record in client service"
              />
            </div>
            <div className="input-field cl-field full-width">
              <label>Skill Summary</label>
              <textarea
                rows="3"
                value={formData.skill_summary}
                onChange={(e) => handleInputChange('skill_summary', e.target.value)}
                placeholder="• Financial modeling and valuation&#10;• Client relationship management&#10;• Cross-functional team leadership"
              />
            </div>
          </section>

          <button type="submit" className="submit-button cl-build-btn" disabled={loading}>
            {loading ? 'Generating Cover Letter...' : 'Generate Cover Letter'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CoverLetterForm;

